import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Input } from '@/Components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/Components/ui/accordion';
import { Search, HelpCircle, Loader2 } from 'lucide-react';
import SEO from '../Components/SEO.jsx';

const categorieLabels = {
    ups: 'UPS e Gruppi di Continuità',
    manutenzione: 'Manutenzione',
    riparazione: 'Riparazioni',
    contratti: 'Contratti',
    batterie: 'Batterie',
    sicurezza: 'Sicurezza',
    generale: 'Generale'
};

const staticFaqs = [
    {
        id: 'static-1',
        categoria: 'ups',
        domanda: 'Cos’è un gruppo di continuità (UPS) e a cosa serve?',
        risposta: '<p>Un <strong>Gruppo di Continuità (UPS)</strong> è un dispositivo che fornisce alimentazione elettrica di emergenza in caso di blackout o cali di tensione. Serve a <strong>proteggere apparecchiature sensibili</strong> (come server, computer, macchinari medici) da danni hardware e perdite di dati, garantendo la continuità operativa.</p>',
        ordine: 1,
        visibile: true
    },
    {
        id: 'static-2',
        categoria: 'ups',
        domanda: 'Qual è la differenza tra UPS online e offline?',
        risposta: '<p>Gli <strong>UPS Offline (Standby)</strong> attivano la batteria solo quando manca la corrente, con un brevissimo tempo di commutazione. Gli <strong>UPS Online (Doppia Conversione)</strong> alimentano sempre il carico tramite l’inverter, garantendo tensione perfetta e tempo di intervento nullo (0ms), ideali per apparecchiature critiche.</p>',
        ordine: 2,
        visibile: true
    },
    {
        id: 'static-3',
        categoria: 'manutenzione',
        domanda: 'Ogni quanto tempo va effettuata la manutenzione di un UPS?',
        risposta: '<p>Si consiglia di effettuare una <strong>manutenzione preventiva almeno una volta l’anno</strong>. Per ambienti critici (ospedali, data center), sono raccomandate due visite annuali. La manutenzione regolare previene guasti improvvisi e allunga la vita operativa dell’apparato.</p>',
        ordine: 1,
        visibile: true
    },
    {
        id: 'static-4',
        categoria: 'manutenzione',
        domanda: 'In cosa consiste la manutenzione di un UPS?',
        risposta: '<p>La manutenzione include: <strong>controllo delle batterie</strong>, pulizia interna da polvere, verifica dei condensatori e delle ventole, test di scarica controllata e aggiornamento firmware. Un controllo accurato può prevenire oltre il 70% dei guasti.</p>',
        ordine: 2,
        visibile: true
    },
    {
        id: 'static-5',
        categoria: 'batterie',
        domanda: 'Quanto durano le batterie di un UPS?',
        risposta: '<p>La vita media delle batterie VRLA standard è di <strong>3-5 anni</strong>. La durata effettiva dipende in modo significativo da due fattori chiave:</p><ul><li><strong>Temperatura ambiente:</strong> il range ideale di funzionamento è tra i 20°C e i 25°C; temperature superiori possono accelerare il degrado chimico.</li><li><strong>Cicli di scarica:</strong> la frequenza e l\'intensità degli interventi dell\'UPS influiscono sulla capacità della batteria di mantenere la carica nel tempo.</li></ul>',
        ordine: 1,
        visibile: true
    },
    {
        id: 'static-6',
        categoria: 'batterie',
        domanda: 'Come smaltire le batterie esauste?',
        risposta: '<p>Le batterie al piombo sono rifiuti speciali pericolosi. <strong>Tecnosys si occupa del ritiro e smaltimento a norma di legge</strong> (Codice CER 160601), rilasciando regolare formulario di identificazione rifiuto (FIR) per garantire il rispetto dell’ambiente.</p>',
        ordine: 2,
        visibile: true
    },
    {
        id: 'static-7',
        categoria: 'sicurezza',
        domanda: 'Gli UPS sono rumorosi in ufficio?',
        risposta: '<p>Gli UPS moderni per ufficio sono progettati per essere silenziosi (<40-50dB). Tuttavia, i modelli industriali o ad alta potenza hanno ventole di raffreddamento che possono generare rumore; in questi casi si consiglia l’installazione in locali tecnici dedicati.</p>',
        ordine: 1,
        visibile: true
    },
    {
        id: 'static-8',
        categoria: 'sicurezza',
        domanda: 'È sicuro tenere un UPS in un ambiente chiuso?',
        risposta: '<p>Sì, ma è fondamentale garantire una <strong>corretta ventilazione</strong>. Le batterie possono emettere piccole quantità di idrogeno (anche se minime nelle VRLA sigillate) e l’elettronica produce calore. Assicurarsi sempre che le prese d’aria non siano ostruite.</p>',
        ordine: 2,
        visibile: true
    }
];

export default function FAQPage() {
    const [searchTerm, setSearchTerm] = useState('');

    const { data: serverFaqs = [], isLoading } = useQuery({
        queryKey: ['faqs'],
        queryFn: () => base44.entities.FAQ.filter({ visibile: true }, 'ordine')
    });

    // Merge server FAQs with static FAQs
    const allFaqs = [...staticFaqs, ...serverFaqs];

    const filteredFaqs = allFaqs.filter(faq =>
        faq.domanda?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.risposta?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <SEO
                title="FAQ - Domande Frequenti su UPS e Manutenzione | Tecnosys"
                description="Trova risposte alle domande più frequenti su UPS, manutenzione, riparazioni e contratti. Supporto completo da Tecnosys."
                keywords="faq ups, domande ups, manutenzione ups, riparazioni, supporto tecnico"
            />

            {/* Hero */}
            <section className="relative bg-gray-900 pt-32 pb-20">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80"
                        alt="FAQ"
                        className="w-full h-full object-cover opacity-20"
                    />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-blue-600 flex items-center justify-center">
                            <HelpCircle className="w-8 h-8 text-white" />
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                            Domande Frequenti
                        </h1>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            Trova rapidamente le risposte alle domande più comuni su UPS,
                            manutenzione, riparazioni e servizi Tecnosys.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Search */}
            <section className="py-8 bg-white border-b">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                            placeholder="Cerca nella FAQ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-12 h-14 rounded-full text-lg"
                        />
                    </div>
                </div>
            </section>

            {/* FAQ Content */}
            <section className="py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        </div>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {filteredFaqs.length > 0 ? (
                                <Accordion type="single" collapsible className="space-y-4">
                                    {filteredFaqs.map((faq) => (
                                        <AccordionItem
                                            key={faq.id}
                                            value={faq.id.toString()}
                                            className="bg-white border rounded-xl px-6"
                                        >
                                            <AccordionTrigger className="text-left hover:no-underline py-4">
                                                <div className="flex flex-col gap-1 text-left">
                                                    <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                                                        {categorieLabels[faq.categoria] || 'Generale'}
                                                    </span>
                                                    <span className="font-semibold text-gray-900">{faq.domanda}</span>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="pb-4">
                                                <div
                                                    className="prose prose-sm max-w-none text-gray-600"
                                                    dangerouslySetInnerHTML={{ __html: faq.risposta }}
                                                />
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            ) : (
                                <p className="text-gray-500 text-center py-8">
                                    Nessuna domanda trovata.
                                </p>
                            )}
                        </motion.div>
                    )}
                </div>
            </section>
        </div>
    );
}